/**
 * Photo colour for scan models: a textured or vertex-coloured mesh shows its
 * captured colour unlit, the way the iOS app's viewer draws it (SceneKit
 * `.constant`). Under the viewer's lights (ambient 0.65 + key 1.1 + fill
 * 0.45) the same atlases read 0.47–0.78× their own brightness, so dark wood
 * and fabric went near-black. Untextured meshes keep the lit material: the
 * shading is their only shape cue.
 */
import * as THREE from "three";

/** Switch: false restores today's lit photo materials. */
export const UNLIT_PHOTO_MATERIALS = true;

function hasColorAttribute(geometry) {
  return Boolean(geometry?.hasAttribute?.("color"));
}

/** True when the material draws a photo (a colour map or per-vertex colours). */
export function isPhotoMaterial(material, geometry) {
  if (!material || material.isMeshBasicMaterial || material.isShaderMaterial) return false;
  if (material.map) return true;
  return Boolean(material.vertexColors && hasColorAttribute(geometry));
}

/**
 * MeshBasicMaterial with the source's map, colour factor and blending. The map
 * is shared, not copied (its colorSpace stays sRGB, so the texel reaches the
 * screen unchanged with the renderer's NoToneMapping).
 */
export function unlitPhotoMaterial(material, geometry) {
  const basic = new THREE.MeshBasicMaterial({
    map: material.map || null,
    alphaMap: material.alphaMap || null,
    color: material.color ? material.color.clone() : new THREE.Color(0xffffff),
    vertexColors: Boolean(material.vertexColors && hasColorAttribute(geometry)),
    side: material.side,
    transparent: Boolean(material.transparent),
    opacity: material.opacity ?? 1,
    alphaTest: material.alphaTest ?? 0,
    depthTest: material.depthTest ?? true,
    depthWrite: material.depthWrite ?? true,
  });
  basic.name = material.name || "";
  return basic;
}

/**
 * Replaces every photo material in `object` by its unlit version. Materials
 * shared between meshes stay shared. The replaced materials are disposed, their
 * maps are not. Returns the number of materials replaced.
 */
export function applyUnlitPhotoMaterials(object) {
  const replaced = new Map();
  object?.traverse((child) => {
    if (!child.isMesh || !child.material) return;
    const geometry = child.geometry;
    const swap = (material) => {
      if (!isPhotoMaterial(material, geometry)) return material;
      const key = hasColorAttribute(geometry) ? "color" : "plain";
      let byKey = replaced.get(material);
      if (!byKey) {
        byKey = {};
        replaced.set(material, byKey);
      }
      if (!byKey[key]) byKey[key] = unlitPhotoMaterial(material, geometry);
      return byKey[key];
    };
    child.material = Array.isArray(child.material) ? child.material.map(swap) : swap(child.material);
  });
  // Detach the maps before disposing, so dispose() cannot reach a shared texture.
  replaced.forEach((_, material) => {
    material.map = null;
    material.alphaMap = null;
    material.dispose();
  });
  return replaced.size;
}
